import { PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { TaskAssigneeOptions } from 'pages/Tasks/constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig, getAdminToAeEmails, getTasks, getUserGroups, setAdminConfig } from 'services'
import { Accordion } from 'stories/components'

import { LoanSubmissionDetails } from './details'
import {
  ActionType,
  actionTypeHeaders,
  actionTypeTitlePrefix,
  actionTypeTitles,
  ILoanActionConfigBase,
  ILoanSubmissionActionConfig,
  isActionTypeHasStatusTo,
} from './types'

export const LoanActions = (props: { setChanged?: Function }) => {
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])

  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Record<ActionType, Record<string, ILoanActionConfigBase>>>()
  const [actionLabels, setActionLabels] = useState<Record<ActionType, Record<string, string>>>()
  const [allSectionsOpen, setAllSectionsOpen] = useState(true)

  const [currentItem, setCurrentItem] = useState<{ type: ActionType | null; key: string | null }>({
    key: null,
    type: null,
  })
  const [adminEmails, setAdminEmails] = useState<string[]>([])
  // User groups are offered as assignees so a team such as Legal can be a routing target without
  // the platform defining an account type for it.
  const [userGroups, setUserGroups] = useState<{ id: number; name: string }[]>([])
  const [tempTasks, setTempTasks] = useState<Array<any>>([])
  const returnToItemRef = useRef<{ type: ActionType; key: string; scrollY: number } | null>(null)

  const orderedKeys = useMemo(() => {
    const rtl = {} as Record<ActionType, string[]>
    // Same sequence as Loan Status Label → Origination. Labels are built from that config order.
    const originationOrder = Object.keys(actionLabels?.[ActionType.LoanStatus] || {})
    const originationIndex = new Map(originationOrder.map((key, index) => [key, index]))
    const byOrigination = (key: string) => {
      const parent = key.split(':')[0]
      return originationIndex.has(parent) ? originationIndex.get(parent)! : Number.MAX_SAFE_INTEGER
    }

    Object.values(ActionType).forEach((type) => {
      const keys = Object.keys(data?.[type] || {}).filter((key) => !!actionLabels?.[type]?.[key])
      if (type === ActionType.LoanStatus || type === ActionType.SubStatus) {
        const labelOrder = Object.keys(actionLabels?.[type] || {}).filter((key) => keys.includes(key))
        rtl[type] = labelOrder.sort((a, b) => byOrigination(a) - byOrigination(b))
        return
      }
      rtl[type] = keys
    })
    return rtl
  }, [data, actionLabels])

  useEffect(() => {
    load()
    getAdminToAeEmails().then((emails: string[]) => setAdminEmails(emails))
    getTasks().then(({ data }) => setTempTasks(data))
    getUserGroups().then((groups: any) => setUserGroups(Array.isArray(groups) ? groups : groups?.data || []))
  }, [])

  // After leaving the detail view, scroll back to the action that was opened.
  useEffect(() => {
    if (currentItem.key || isLoading) return
    const pending = returnToItemRef.current
    if (!pending) return
    returnToItemRef.current = null

    const scrollToItem = () => {
      const el = document.getElementById(`loan-action-row-${pending.type}-${pending.key}`)
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'auto' })
      } else {
        window.scrollTo(0, pending.scrollY)
      }
    }

    requestAnimationFrame(() => requestAnimationFrame(scrollToItem))
  }, [currentItem.key, isLoading])

  const load = async () => {
    setLoading(true)
    getAdminConfig('loanActions')
      .then(({ data, labels }) => {
        setData(data)
        setActionLabels(labels)
      })
      .finally(() => setLoading(false))
  }

  const openItem = (type: ActionType, key: string) => {
    returnToItemRef.current = { type, key, scrollY: window.scrollY }
    setCurrentItem({ type, key })
  }

  // When opening an action detail, always start at the top of the page.
  useEffect(() => {
    if (!currentItem.key) return
    window.scrollTo(0, 0)
  }, [currentItem.key, currentItem.type])

  const onBack = (values: ILoanActionConfigBase | null) => {
    props.setChanged?.(false)
    if (values && currentItem.key) {
      setLoading(true)
      const newData = cloneDeep(data!)
      newData[currentItem.type!][currentItem.key] = values
      setData(newData)
      setAdminConfig(`loanActions/${currentItem.type!}`, newData[currentItem.type!]).finally(() => setLoading(false))
    }
    setCurrentItem({ key: null, type: null })
  }

  const onChangeOrder = async (toIndex: number, fromIndex: number, type: ActionType) => {
    if (toIndex == fromIndex) return

    const newKeys = [...orderedKeys[type]]
    const inactiveKeys = Object.keys(data![type]).filter((key) => !newKeys.includes(key))

    const [movedKey] = newKeys.splice(fromIndex, 1)
    newKeys.splice(toIndex, 0, movedKey)

    newKeys.push(...inactiveKeys)
    const reorder: any = {}
    newKeys.forEach((key) => {
      reorder[key] = data![type][key]
    })

    const newData = cloneDeep(data!)
    newData[type] = reorder
    setData(newData)

    setLoading(true)
    await setAdminConfig(`loanActions/${type!}`, reorder)
    setLoading(false)
  }

  const getTaskAssigneeOption = (to: string) => {
    if (TaskAssigneeOptions()[to]) return TaskAssigneeOptions()[to]
    if (to?.startsWith('group:')) {
      const group = userGroups.find((g) => `group:${g.id}` === to)
      return group ? `[Group] ${group.name}` : ''
    }
    if (adminEmails.includes(to)) return to
    return ''
  }

  const getStatusChangeRuleLabels = (item: ILoanSubmissionActionConfig) => {
    const rules = item.statusChangeRules?.length
      ? item.statusChangeRules
      : item.statusTo
      ? [{ statusTo: item.statusTo }]
      : []

    if (!rules.length) return '- No Change -'

    return rules
      .map((rule) => (rule.statusTo ? loanGlobalStatus[rule.statusTo] || rule.statusTo : '- No Change -'))
      .join(', ')
  }

  const renderItem = (key: string, item: ILoanSubmissionActionConfig, index: number, type: ActionType) => {
    const cn = `${index % 2 ? 'bg-slate-50' : 'bg-white'}`

    const hasStatusTo = isActionTypeHasStatusTo(type)

    const tasks = (
      <>
        {item.tasks?.map((task, index) => {
          return (
            <div key={`task-${index} mb-2`} className="flex">
              <div className="w-[20px]">{index + 1}. </div>
              <div>
                <div>Description: {task.description}</div>
                <div>
                  Assigned to:{' '}
                  {task.assignedTo
                    .map((to: string) => getTaskAssigneeOption(to))
                    .filter((v: string) => !!v)
                    .join(', ')}
                </div>
                {!!task.assignedToSecondary?.length && (
                  <div>
                    Secondary:{' '}
                    {task.assignedToSecondary
                      .map((to: string) => getTaskAssigneeOption(to))
                      .filter((v: string) => !!v)
                      .join(', ')}
                  </div>
                )}
                <div>Due Days: {task.dueDays}</div>
              </div>
            </div>
          )
        })}
      </>
    )

    return [
      <tr className={cn} key={`${index}`} id={`loan-action-row-${type}-${key}`}>
        <td className="px-3 py-3" rowSpan={hasStatusTo ? 2 : 1}>
          {index + 1}
        </td>
        <td className="px-3 py-2" rowSpan={hasStatusTo ? 2 : 1}>
          {actionLabels?.[type][key]}
        </td>
        {hasStatusTo ? <td className="px-3">{getStatusChangeRuleLabels(item)}</td> : <td className="px-3">{tasks}</td>}
        <td>
          <span className={`flex px-3 ${hasStatusTo ? '' : 'justify-end'}`}>
            <span
              className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
              onClick={() => openItem(type, key)}
            >
              <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
            </span>
          </span>
        </td>
        {hasStatusTo && (
          <td className="px-1 text-right">
            <select
              value={index}
              className="text-sm py-1 border-gray-200 pr-2 w-fit"
              onChange={(e) => onChangeOrder(parseInt(e.target.value), index, type)}
            >
              {orderedKeys[type].map((_, value) => (
                <option value={value} key={`option-${value}`}>
                  {value + 1}
                </option>
              ))}
            </select>
          </td>
        )}
      </tr>,

      hasStatusTo ? (
        <tr className={`${cn} border-b italic`} key={`2-${index}`}>
          <td colSpan={hasStatusTo ? 3 : 1} className="p-2">
            {tasks}
          </td>
        </tr>
      ) : null,
    ].filter((v) => !!v)
  }

  if (!data)
    return (
      <div className="relative px-3 py-1.5">
        <LayoutLoading show={isLoading} />
      </div>
    )

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />

      {!currentItem.key ? (
        <div className="w-full">
          <div className="flex justify-end mb-2">
            <p
              className="text-sm cursor-pointer text-shade-blue hover:underline"
              onClick={() => setAllSectionsOpen((open) => !open)}
            >
              {allSectionsOpen ? 'Collapse All' : 'Expand All'}
            </p>
          </div>
          {Object.values(ActionType).map((actionKey, index) => (
            <Accordion
              key={`${actionKey}-${allSectionsOpen}`}
              defaultOpen={allSectionsOpen}
              title={
                <p>
                  {index + 1}. {actionTypeTitles[actionKey]}
                </p>
              }
            >
              <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 mb-4">
                <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    {actionTypeHeaders[actionKey].map((v, index) => (
                      <th
                        key={v}
                        scope="col"
                        className={`px-3 py-3 ${index == actionTypeHeaders[actionKey].length - 1 ? 'text-right' : ''}`}
                      >
                        {v}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="">
                  {orderedKeys[actionKey].map((key, index) =>
                    renderItem(key, data![actionKey][key] as any, index, actionKey),
                  )}
                </tbody>
              </table>
            </Accordion>
          ))}
        </div>
      ) : (
        <LoanSubmissionDetails
          actionType={currentItem.type!}
          action={currentItem.key}
          label={`${actionTypeTitlePrefix[currentItem.type!] || ''} ${
            actionLabels![currentItem.type!][currentItem.key]
          }`}
          data={data![currentItem.type!][currentItem.key] as any}
          adminEmails={adminEmails}
          userGroups={userGroups}
          tempTasks={tempTasks}
          onBack={onBack}
          onChanged={props.setChanged}
        />
      )}
    </div>
  )
}
